import {
    ChevronRight,
    Circle,
    FlagCircle,
    VisibilityOff,
} from "@mui/icons-material";
import {
    Box,
    Divider,
    IconButton,
    Menu,
    MenuItem,
    Paper,
    Typography,
} from "@mui/material";
import {
    EnabledFeatureFlags,
    FeatureFlagsContext,
} from "components/providers/featureFlags/featureFlagContext";
import { featureFlags } from "components/providers/featureFlags/flags";
import { useSetFeatureFlags } from "components/providers/featureFlags/useFeatureFlags";
import { useContext, useState } from "react";
import CONSTANTS from "src/constants";
import { Sx } from "utils/sx";

const styles: Sx = (theme) => ({
    margin: theme.spacing(1),
    position: "fixed",
    bottom: 0,
    left: 0,
    zIndex: theme.zIndex.tooltip + 1,
    transition: "opacity 0.3s ease-in-out",
    opacity: 0.3,
    "&:hover": {
        opacity: 1,
    },
    "& .hidden": {
        opacity: 0,
        transition: "opacity 0.15s ease-in-out",
    },
    "& .unhidden": {
        transition: "opacity 0.15s ease-in-out",
    },
    "&:hover .hidden": {
        opacity: 1,
    },
    "&:hover .unhidden": {
        opacity: 0,
    },
});

const iconButtonStyles: Sx = {
    display: "inline-grid",
    "& > *": {
        gridColumn: 1,
        gridRow: 1,
    },
    borderRadius: 1,
};

const menuStyles: Sx = (theme) => ({
    zIndex: theme.zIndex.tooltip + 2,
});

const menuItemStyles: (selected: boolean) => Sx = (selected) => (theme) => ({
    py: 0.5,
    backgroundColor: selected ? theme.palette.secondary.main : "transparent",
    "&:hover": {
        backgroundColor: selected ? theme.palette.secondary.light : undefined,
    },
});

const menuItemBoxStyles: Sx = {
    width: "100%",
    display: "flex",
    alignItems: "center",
    gap: 1,
};

const subMenuItemStyles: (selected: boolean) => Sx = (selected) => (theme) => ({
    py: 0.5,
    backgroundColor: selected ? theme.palette.secondary.main : "transparent",
    "&:hover": {
        backgroundColor: selected ? theme.palette.secondary.light : undefined,
    },
});

export const FeatureFlagsToggle = () => {
    const [flags] = useContext(FeatureFlagsContext);
    const { toggle } = useSetFeatureFlags();

    const [hideComponent, setHideComponent] = useState(false);

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [subMenuAnchorEl, setSubMenuAnchorEl] = useState<null | HTMLElement>(
        null,
    );
    const [openEnumMenu, setOpenEnumMenu] =
        useState<keyof EnabledFeatureFlags>();

    const isDev = CONSTANTS.DEV;
    const isStaging = window.location.hostname.includes("staging.liveheats");
    const isProd = !isDev && !isStaging;

    if (isProd || hideComponent) return null;

    const open = Boolean(anchorEl);
    const subMenuOpen = Boolean(subMenuAnchorEl);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
        setSubMenuAnchorEl(null);
    };

    const handleSubMenuClick = (
        event: React.MouseEvent<HTMLElement>,
        key: keyof EnabledFeatureFlags,
    ) => {
        setSubMenuAnchorEl(event.currentTarget);
        setOpenEnumMenu(key);
    };

    const handleSubMenuClose = () => {
        setSubMenuAnchorEl(null);
    };

    const enabledFlagCount = Object.keys(flags).filter(
        (key) => flags[key as keyof EnabledFeatureFlags],
    ).length;
    const enumMenuOptions = (featureFlags[openEnumMenu!] as unknown as string[]) || [];

    return (
        <Box sx={styles}>
            <Paper elevation={1}>
                <IconButton
                    aria-label="Feature Flags"
                    sx={iconButtonStyles}
                    onClick={handleClick}
                >
                    {enabledFlagCount > 0 && (
                        <>
                            <Circle className="unhidden" />
                            <Typography
                                className="unhidden"
                                variant="caption"
                                fontWeight={600}
                                color="textContrastText"
                            >
                                {enabledFlagCount}
                            </Typography>
                        </>
                    )}
                    <FlagCircle
                        className={enabledFlagCount > 0 ? "hidden" : ""}
                    />
                </IconButton>
                <Menu
                    sx={menuStyles}
                    anchorEl={anchorEl}
                    anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
                    transformOrigin={{ horizontal: "left", vertical: "bottom" }}
                    open={open}
                    onClose={handleClose}
                >
                    <Box sx={{ display: "flex", alignItems: "center", px: 1 }}>
                        <Typography
                            sx={{ px: 1 }}
                            color="textSecondary"
                            variant="label2"
                            textTransform="uppercase"
                        >
                            Feature Flags
                        </Typography>
                        <IconButton onClick={() => setHideComponent(true)}>
                            <VisibilityOff fontSize="small" />
                        </IconButton>
                    </Box>
                    <Divider sx={{ my: 1 }} />
                    {Object.entries<string[] | boolean>(featureFlags).map(
                        ([_key, value]) => {
                            const key = _key as keyof EnabledFeatureFlags;
                            const flagValue = flags[key];
                            const selected = !!flagValue;
                            const hasSubMenu = Array.isArray(value);

                            return (
                                <MenuItem
                                    key={key}
                                    sx={menuItemStyles(selected)}
                                    onClick={(e) =>
                                        hasSubMenu
                                            ? handleSubMenuClick(e, key)
                                            : toggle(key)
                                    }
                                >
                                    <Box sx={menuItemBoxStyles}>
                                        <Typography
                                            variant="caption"
                                            fontWeight={500}
                                        >
                                            {key}
                                        </Typography>
                                        {typeof flagValue === "string" && (
                                            <Typography
                                                variant="caption"
                                                color="textHighlight"
                                            >
                                                {flagValue}
                                            </Typography>
                                        )}
                                        {hasSubMenu && (
                                            <ChevronRight
                                                fontSize="small"
                                                sx={{ ml: "auto" }}
                                            />
                                        )}
                                    </Box>
                                </MenuItem>
                            );
                        },
                    )}

                    <Menu
                        sx={menuStyles}
                        anchorEl={subMenuAnchorEl}
                        open={subMenuOpen}
                        onClose={handleSubMenuClose}
                        anchorOrigin={{
                            horizontal: "right",
                            vertical: "bottom",
                        }}
                    >
                        {enumMenuOptions.map((option) => {
                            const selected = (flags[openEnumMenu!] as unknown as string) === option;

                            return (
                                <MenuItem
                                    key={option}
                                    sx={subMenuItemStyles(selected)}
                                    onClick={() => {
                                        handleSubMenuClose();
                                        if (openEnumMenu)
                                            toggle(openEnumMenu, option);
                                    }}
                                >
                                    <Typography variant="caption">
                                        {option}
                                    </Typography>
                                </MenuItem>
                            );
                        })}
                    </Menu>
                </Menu>
            </Paper>
        </Box>
    );
};
